// Deterministic models of CollabBoard's layers for Lesson 2.12 ("Give Each Part One Job").
// They sort lines of code into routes, services and the repository, check which way imports
// point, follow the transactions of two connection scopes and replay a five-move refactor;
// they run no Python, open no connection and send no requests.

export type Tone = 'neutral' | 'success' | 'warning';

const natural = (items: (string | number)[]) =>
  items.length <= 1
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${String(items.at(-1))}`;
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

// --- layer-sort: which part should own this line ----------------------------------------

export type Layer = 'routes' | 'services' | 'repository';
export const LAYERS: readonly Layer[] = ['routes', 'services', 'repository'];
export const layerLabels: Record<Layer, string> = {
  routes: 'Routes',
  services: 'Services',
  repository: 'Repository',
};
export const layerFiles: Record<Layer, string> = {
  routes: 'routes.py',
  services: 'services.py',
  repository: 'repository.py',
};
/** The one question each part answers. */
export const layerQuestions: Record<Layer, string> = {
  routes: 'what does HTTP say?',
  services: 'is it allowed, and what happens?',
  repository: 'which rows?',
};
export const LAYERS_SUMMARY = LAYERS.map(
  (layer) => `${layerLabels[layer]}: ${layerQuestions[layer]}`,
).join(' ');

export type LineId =
  | 'set-cookie'
  | 'require'
  | 'select'
  | 'http-401'
  | 'hash'
  | 'query-limit'
  | 'last-manager'
  | 'order-limit';
export interface SortLine {
  id: LineId;
  code: string;
  owner: Layer;
  /** Why the owner is right, shown once the line is placed correctly. */
  why: string;
  /** One sentence for each wrong placement. */
  wrong: Partial<Record<Layer, string>>;
}
export const SORT_LINES: readonly SortLine[] = [
  {
    id: 'set-cookie',
    code: 'response.set_cookie(COOKIE, token, ...)',
    owner: 'routes',
    why: 'A cookie is a header on the HTTP response.',
    wrong: {
      services:
        'set_cookie writes an HTTP header: a service would then need a FastAPI Response to run.',
      repository:
        'A cookie is part of the HTTP response, not a row: the repository only reads and writes tables.',
    },
  },
  {
    id: 'require',
    code: 'permissions.require(role, "delete_task")',
    owner: 'services',
    why: 'Whether this person may delete the task is a rule.',
    wrong: {
      routes:
        'require is a rule, not HTTP: in a route it would guard only HTTP callers, and every route that deletes would have to repeat it.',
      repository:
        'require decides who may act: the repository answers only which rows, so it never refuses anyone.',
    },
  },
  {
    id: 'select',
    code: 'SELECT t.id, t.title FROM tasks AS t WHERE ...',
    owner: 'repository',
    why: "Which rows to read is the repository's question.",
    wrong: {
      routes:
        'SQL in a route ties HTTP to the table layout: renaming a column would mean editing routes.',
      services:
        'SQL in a service mixes the rules with the table layout: the rules could no longer be tested without a database.',
    },
  },
  {
    id: 'http-401',
    code: 'raise HTTPException(401, "Log in first.")',
    owner: 'routes',
    why: 'A status code is what HTTP says.',
    wrong: {
      services: 'HTTPException is HTTP: a service would then only work behind FastAPI.',
      repository: 'HTTPException is HTTP: a query that raised it could only run inside a request.',
    },
  },
  {
    id: 'hash',
    code: 'auth.hash_password(password)',
    owner: 'services',
    why: 'Storing a hash, never the password, is a rule of registering.',
    wrong: {
      routes:
        'Hashing is a rule of registering, not HTTP: anything that registers a user without the route would skip it.',
      repository:
        'The repository stores the hash it is given: hashing there would hide a rule inside data access.',
    },
  },
  {
    id: 'query-limit',
    code: 'limit: int = Query(50, ge=1, le=100)',
    owner: 'routes',
    why: "It reads and checks the URL's query string.",
    wrong: {
      services:
        "Query(...) reads the URL's query string: a service receives limit as a plain number, already checked.",
      repository:
        "Query(...) parses the request's URL: the repository receives limit as a plain argument for LIMIT %s.",
    },
  },
  {
    id: 'last-manager',
    code: 'if count_managers(connection, project_id) == 0: raise LastManager(...)',
    owner: 'services',
    why: 'A project must keep a manager: that decides what may happen.',
    wrong: {
      routes:
        'The last-manager rule is not HTTP: in a route, only HTTP callers would be stopped from removing the last manager.',
      repository:
        'count_managers is the query, but refusing when it is zero is a rule: the repository returns the count and decides nothing.',
    },
  },
  {
    id: 'order-limit',
    code: 'ORDER BY t.id LIMIT %s',
    owner: 'repository',
    why: 'Which rows come back, and in which order, is part of the query.',
    wrong: {
      routes:
        'ORDER BY and LIMIT are SQL: the route passes limit as a number and never writes SQL.',
      services:
        'Order and page size belong to the query: the service passes limit and after, and receives the rows.',
    },
  },
];
export const LINE_COUNT = SORT_LINES.length;

export type Answer = Layer | '';
export type SortAnswers = Record<LineId, Answer>;
export interface SortState {
  answers: SortAnswers;
  /** True after Check until any answer changes. */
  checked: boolean;
}
export type SortAction =
  { kind: 'answer'; line: LineId; layer: Layer } | { kind: 'check' } | { kind: 'reset' };
export const initialSort = (): SortState => ({
  answers: Object.fromEntries(SORT_LINES.map((line) => [line.id, ''])) as SortAnswers,
  checked: false,
});
export function sortTransition(state: SortState, action: SortAction): SortState {
  switch (action.kind) {
    case 'reset':
      return initialSort();
    case 'answer':
      return { answers: { ...state.answers, [action.line]: action.layer }, checked: false };
    case 'check':
      return { ...state, checked: true };
  }
}

export type LineVerdict = 'right' | 'wrong' | 'unanswered';
export interface LineResult {
  line: SortLine;
  /** 1-based position in the list. */
  number: number;
  answer: Answer;
  verdict: LineVerdict;
  /** What the line shows after Check. */
  text: string;
  /** Why a wrong placement is wrong; empty otherwise. */
  reason: string;
}
export function gradeLine(line: SortLine, answer: Answer, number: number): LineResult {
  if (answer === '')
    return { line, number, answer, verdict: 'unanswered', text: 'No part chosen yet.', reason: '' };
  if (answer === line.owner)
    return { line, number, answer, verdict: 'right', text: `Right. ${line.why}`, reason: '' };
  const reason = line.wrong[answer] ?? '';
  return {
    line,
    number,
    answer,
    verdict: 'wrong',
    text: `Not ${layerLabels[answer]}. ${reason}`,
    reason,
  };
}
export interface SortReport {
  results: LineResult[];
  answered: number;
  right: number;
  wrong: LineResult[];
  unanswered: number;
  /** True only after Check with every line right. */
  solved: boolean;
  message: string;
  tone: Tone;
}
export const SORT_PROMPT = 'Choose Routes, Services or Repository for each line, then press Check.';
export function sortReport(state: SortState): SortReport {
  const results = SORT_LINES.map((line, index) =>
    gradeLine(line, state.answers[line.id], index + 1),
  );
  const right = results.filter((result) => result.verdict === 'right').length;
  const wrong = results.filter((result) => result.verdict === 'wrong');
  const unanswered = results.filter((result) => result.verdict === 'unanswered').length;
  const base = { results, answered: LINE_COUNT - unanswered, right, wrong, unanswered };
  if (!state.checked) return { ...base, solved: false, message: SORT_PROMPT, tone: 'neutral' };
  if (right === LINE_COUNT)
    return {
      ...base,
      solved: true,
      message: `All ${LINE_COUNT} lines are in the right part. ${LAYERS_SUMMARY}`,
      tone: 'success',
    };
  const reasons = wrong.map(
    (result) =>
      `Line ${result.number} does not belong in ${result.answer ? layerLabels[result.answer] : ''}. ${result.reason}`,
  );
  const missing = unanswered
    ? [`${plural(unanswered, 'line')} ${unanswered === 1 ? 'has' : 'have'} no part yet.`]
    : [];
  return {
    ...base,
    solved: false,
    message: [
      `${right} of ${LINE_COUNT} lines are in the right part.`,
      ...reasons,
      ...missing,
    ].join(' '),
    tone: wrong.length ? 'warning' : 'neutral',
  };
}
/** The lines a correct sort puts in each part. */
export const linesOf = (layer: Layer) =>
  SORT_LINES.filter((line) => line.owner === layer).map((line) => line.code);

// --- dependency-direction: which way may imports point ----------------------------------

export type Module = Layer;
export type ImportId =
  | 'services-routes'
  | 'repository-services'
  | 'routes-repository'
  | 'services-repository'
  | 'routes-services';
export type ImportKind = 'down' | 'backward' | 'skip';
export interface ImportSpec {
  id: ImportId;
  from: Module;
  to: Module;
  label: string;
  kind: ImportKind;
}
export const IMPORTS: readonly ImportSpec[] = [
  {
    id: 'services-routes',
    from: 'services',
    to: 'routes',
    label: 'services imports routes',
    kind: 'backward',
  },
  {
    id: 'repository-services',
    from: 'repository',
    to: 'services',
    label: 'repository imports services',
    kind: 'backward',
  },
  {
    id: 'routes-repository',
    from: 'routes',
    to: 'repository',
    label: 'routes imports repository',
    kind: 'skip',
  },
  {
    id: 'services-repository',
    from: 'services',
    to: 'repository',
    label: 'services imports repository',
    kind: 'down',
  },
  {
    id: 'routes-services',
    from: 'routes',
    to: 'services',
    label: 'routes imports services',
    kind: 'down',
  },
];
const IMPORT_BY_ID = Object.fromEntries(IMPORTS.map((entry) => [entry.id, entry])) as Record<
  ImportId,
  ImportSpec
>;
export const importSpec = (id: ImportId) => IMPORT_BY_ID[id];
export type ImportSet = Record<ImportId, boolean>;
export const initialImports = (): ImportSet => ({
  'services-routes': false,
  'repository-services': false,
  'routes-repository': false,
  'services-repository': true,
  'routes-services': true,
});
/** The three layers, top to bottom. */
export const STACK: readonly Module[] = ['routes', 'services', 'repository'];
/** Modules every layer may import; they import none of the three layers. */
export const BELOW = ['permissions.py', 'settings.py', 'database.py'] as const;

/** Every simple import cycle, each listed once from its topmost module and closed. */
export function findCycles(on: ImportSet): Module[][] {
  const edges = IMPORTS.filter((entry) => on[entry.id]);
  const cycles: Module[][] = [];
  STACK.forEach((start, startIndex) => {
    const walk = (path: Module[]) => {
      const last = path.at(-1) ?? start;
      for (const edge of edges.filter((entry) => entry.from === last)) {
        if (edge.to === start) cycles.push([...path, start]);
        else if (STACK.indexOf(edge.to) > startIndex && !path.includes(edge.to))
          walk([...path, edge.to]);
      }
    };
    walk([start]);
  });
  // Shortest first within each starting module, so the two-module cycles lead.
  return cycles.sort(
    (one, two) =>
      STACK.indexOf(one[0] ?? 'routes') - STACK.indexOf(two[0] ?? 'routes') ||
      one.length - two.length,
  );
}
export const cycleText = (cycle: readonly Module[]) => cycle.join(' → ');
const inCycle = (cycles: Module[][], from: Module, to: Module) =>
  cycles.some((cycle) => cycle.some((module, index) => module === from && cycle[index + 1] === to));

export const ALLOWED_MESSAGE =
  'Allowed: every import points down, so each layer can be tested without the layers above it.';
export const SKIP_MESSAGE =
  'Allowed by Python, but the route now skips the rules: a permission check can be bypassed.';
export interface DependencyReport {
  enabled: ImportSpec[];
  cycles: Module[][];
  /** Imports that point up or skip a layer. */
  problems: ImportSpec[];
  message: string;
  tone: Tone;
}
export function dependencyReport(on: ImportSet): DependencyReport {
  const enabled = IMPORTS.filter((entry) => on[entry.id]);
  const cycles = findCycles(on);
  const problems = enabled.filter((entry) => entry.kind !== 'down');
  const messages: string[] = [];
  if (on['services-routes']) {
    const lead = 'HTTP leaks into the rules: services would need a request to run';
    messages.push(
      on['routes-services']
        ? `${lead}, and routes already import services, so the two import each other (a cycle).`
        : inCycle(cycles, 'services', 'routes')
          ? `${lead}, and routes already reach services through the repository, so the imports go round in a cycle.`
          : `${lead}.`,
    );
  }
  if (on['repository-services']) {
    const lead = 'SQL would depend on rules: the repository could not run without services';
    messages.push(
      on['services-repository']
        ? `${lead}, and services already import the repository, so the two import each other (a cycle).`
        : inCycle(cycles, 'repository', 'services')
          ? `${lead}, and services already reach the repository through routes, so the imports go round in a cycle.`
          : `${lead}.`,
    );
  }
  if (on['routes-repository']) messages.push(SKIP_MESSAGE);
  if (problems.length)
    return { enabled, cycles, problems, message: messages.join(' '), tone: 'warning' };
  const gaps = [
    ...(on['routes-services']
      ? []
      : ['With routes imports services off, no route can call a rule.']),
    ...(on['services-repository']
      ? []
      : ['With services imports repository off, the rules cannot read or write rows.']),
  ];
  return {
    enabled,
    cycles,
    problems,
    message: [ALLOWED_MESSAGE, ...gaps].join(' '),
    tone: 'success',
  };
}
export const enabledText = (report: DependencyReport) =>
  report.enabled.length ? report.enabled.map((entry) => entry.label).join('; ') : 'none';
export const cyclesText = (report: DependencyReport) =>
  report.cycles.length ? report.cycles.map(cycleText).join('; ') : 'none';

// --- connection-scope: what can a test undo ---------------------------------------------

export type Scope = 'per-function' | 'per-request';
export const scopeLabels: Record<Scope, string> = {
  'per-function': 'Each function opens a connection',
  'per-request': 'One connection per request',
};
export const SCOPES = Object.keys(scopeLabels) as Scope[];
export type ScopeRun = 'delete' | 'test';
export const runLabels: Record<ScopeRun, string> = {
  delete: 'Ravi deletes task 10',
  test: 'A test creates a task, then rolls back',
};
export const SCOPE_RUNS = Object.keys(runLabels) as ScopeRun[];
export interface ScopeTask {
  id: number;
  title: string;
}
/** Apollo's tasks in the lesson dataset. */
export const LAUNCH_TASKS: readonly ScopeTask[] = [
  { id: 9, title: 'Write the release note' },
  { id: 10, title: 'Check the dates' },
  { id: 12, title: 'Draft the agenda' },
  { id: 13, title: 'Send the invites' },
  { id: 14, title: 'Print the badges' },
  { id: 15, title: 'Confirm the venue' },
];
export const TEST_TASK: ScopeTask = { id: 16, title: 'Test task' };
export interface TimelineStep {
  transaction: string;
  step: string;
}
export interface ScopeOutcome {
  timeline: TimelineStep[];
  transactions: number;
  commits: number;
  tasksAfter: ScopeTask[];
  /** The fact that changes with the run, as [label, value]. */
  fact: [string, string];
  message: string;
  tone: Tone;
}
const ROLE_CHECK = 'SELECT role FROM memberships WHERE project_id = 1 AND user_id = 2 → "manager"';
const DELETE = 'DELETE FROM tasks WHERE id = 10 → 1 row';
const INSERT = `INSERT INTO tasks (project_id, title) VALUES (1, 'Test task') → task ${TEST_TASK.id}`;
export const GAP_STEP = 'Gap between transactions: a demotion committed now is missed';
export function scopeOutcome(scope: Scope, run: ScopeRun): ScopeOutcome {
  const withoutTen = LAUNCH_TASKS.filter((task) => task.id !== 10);
  if (run === 'delete' && scope === 'per-function')
    return {
      timeline: [
        { transaction: 'T1 · connection 1', step: ROLE_CHECK },
        { transaction: 'T1 · connection 1', step: 'COMMIT; connection 1 closes' },
        { transaction: '(none)', step: GAP_STEP },
        { transaction: 'T2 · connection 2', step: DELETE },
        { transaction: 'T2 · connection 2', step: 'COMMIT; connection 2 closes' },
      ],
      transactions: 2,
      commits: 2,
      tasksAfter: withoutTen,
      fact: ['Task 10', 'deleted by T2'],
      message:
        'Two transactions: T1 committed the role check and closed before T2 deleted task 10. A demotion committed between them is missed: T2 deletes on the strength of a check that has already ended.',
      tone: 'warning',
    };
  if (run === 'delete')
    return {
      timeline: [
        { transaction: "T1 · the request's connection", step: ROLE_CHECK },
        { transaction: "T1 · the request's connection", step: DELETE },
        {
          transaction: "T1 · the request's connection",
          step: 'The route returns: get_connection commits, then closes',
        },
      ],
      transactions: 1,
      commits: 1,
      tasksAfter: withoutTen,
      fact: ['Task 10', 'deleted by T1'],
      message:
        'One transaction: the role check and the delete ran on the connection get_connection opened for this request, and they commit together after the route returns. Had the route raised, both would roll back.',
      tone: 'success',
    };
  if (scope === 'per-function')
    return {
      timeline: [
        { transaction: 'Test · connection A', step: 'The test opens its own transaction' },
        {
          transaction: 'T1 · connection 1',
          step: `The route's function opens connection 1: ${INSERT}`,
        },
        { transaction: 'T1 · connection 1', step: 'COMMIT; connection 1 closes' },
        {
          transaction: 'Test · connection A',
          step: 'ROLLBACK: connection A wrote nothing, so nothing is undone',
        },
      ],
      transactions: 2,
      commits: 1,
      tasksAfter: [...LAUNCH_TASKS, TEST_TASK],
      fact: [`Task ${TEST_TASK.id}`, 'still in the table'],
      message: `Task ${TEST_TASK.id} is still in the table after the rollback: the route's function committed on its own connection. The test's rollback reached only connection A, so every later test starts with an extra task.`,
      tone: 'warning',
    };
  return {
    timeline: [
      {
        transaction: 'Test · connection A',
        step: 'The test opens a transaction and passes connection A down, as get_connection does for a request',
      },
      {
        transaction: 'Test · connection A',
        step: `services.create_task(connection, ...) → repository.insert_task(connection, ...): ${INSERT}`,
      },
      { transaction: 'Test · connection A', step: `ROLLBACK: task ${TEST_TASK.id} is undone` },
    ],
    transactions: 1,
    commits: 0,
    tasksAfter: [...LAUNCH_TASKS],
    fact: [`Task ${TEST_TASK.id}`, 'gone after the rollback'],
    message: `Task ${TEST_TASK.id} is gone after the rollback: the insert ran on the test's connection, inside the test's transaction, because every function receives the connection instead of opening its own. The table is as the test found it.`,
    tone: 'success',
  };
}
export interface ScopeState {
  scope: Scope;
  run: ScopeRun | null;
}
export type ScopeAction =
  { kind: 'set-scope'; scope: Scope } | { kind: 'run'; run: ScopeRun } | { kind: 'reset' };
export const initialScope = (): ScopeState => ({ scope: 'per-function', run: null });
export function scopeTransition(state: ScopeState, action: ScopeAction): ScopeState {
  switch (action.kind) {
    case 'reset':
      return initialScope();
    case 'set-scope':
      return { scope: action.scope, run: null };
    case 'run':
      return { ...state, run: action.run };
  }
}
export const SCOPE_PROMPT = `Choose a scope, then press ${runLabels.delete} or ${runLabels.test}. Every run starts from the lesson data.`;

// --- refactor-steps: where is the mistake found -----------------------------------------

export type Plan = 'big-bang' | 'small-steps';
export const planLabels: Record<Plan, string> = {
  'big-bang': 'Move everything, then compare',
  'small-steps': 'One move, then compare',
};
export const PLANS = Object.keys(planLabels) as Plan[];
export const MOVES = [
  '1. settings.py',
  '2. one connection per request',
  '3. repository.py',
  '4. services.py',
  '5. routes.py',
] as const;
export const MOVE_COUNT = MOVES.length;
/** The move whose paging query loses ORDER BY t.id. */
export const MISTAKE_MOVE = 3;
export const REQUEST_COUNT = 34;
export const CHANGED_REQUEST = 'GET /projects/{project}/tasks?limit=5 as ravi';
export const SAME = `Same behavior: ${REQUEST_COUNT} requests.`;
export const CHANGED = `Changed: 1 of ${REQUEST_COUNT} requests (${CHANGED_REQUEST})`;
export const NOT_COMPARED = 'Moved; not compared';
export const NOT_MOVED = 'Not moved yet';
export const STOPPED = `Not run: stopped at move ${MISTAKE_MOVE}`;

export interface RefactorState {
  plan: Plan;
  /** Moves made so far, 0 to 5. */
  done: number;
}
export type RefactorAction =
  { kind: 'set-plan'; plan: Plan } | { kind: 'next' } | { kind: 'run' } | { kind: 'reset' };
export const initialRefactor = (): RefactorState => ({ plan: 'big-bang', done: 0 });
/** Small steps stop at the first changed comparison; the big bang runs all five moves. */
export const lastMove = (plan: Plan) => (plan === 'small-steps' ? MISTAKE_MOVE : MOVE_COUNT);
export const refactorFinished = (state: RefactorState) => state.done >= lastMove(state.plan);
export function refactorTransition(state: RefactorState, action: RefactorAction): RefactorState {
  switch (action.kind) {
    case 'reset':
      return initialRefactor();
    case 'set-plan':
      return { plan: action.plan, done: 0 };
    case 'next':
      return refactorFinished(state) ? state : { ...state, done: state.done + 1 };
    case 'run':
      return { ...state, done: lastMove(state.plan) };
  }
}
/** The comparison shown after one move: 1-based move number. */
export function compareAfter(state: RefactorState, move: number) {
  if (move > state.done)
    return state.plan === 'small-steps' && refactorFinished(state) ? STOPPED : NOT_MOVED;
  if (state.plan === 'big-bang') return move === MOVE_COUNT ? CHANGED : NOT_COMPARED;
  return move >= MISTAKE_MOVE ? CHANGED : SAME;
}
export const refactorRows = (state: RefactorState) =>
  MOVES.map((move, index) => [move, compareAfter(state, index + 1)]);
export const comparisons = (state: RefactorState) =>
  state.plan === 'small-steps' ? state.done : state.done === MOVE_COUNT ? 1 : 0;
export function suspects(state: RefactorState) {
  if (comparisons(state) === 0) return '(no comparison yet)';
  if (state.plan === 'big-bang') return natural(MOVES.map((_, index) => index + 1));
  return state.done >= MISTAKE_MOVE ? String(MISTAKE_MOVE) : 'none so far';
}
export const REFACTOR_PROMPT =
  'Choose a plan, then press Next move or Run the plan. One of the five moves hides a mistake.';
export function refactorMessage(state: RefactorState): { message: string; tone: Tone } {
  if (state.done === 0) return { message: REFACTOR_PROMPT, tone: 'neutral' };
  if (state.plan === 'big-bang') {
    if (state.done < MOVE_COUNT)
      return {
        message: `Move ${state.done} made. This plan compares only after all five moves, so nothing has been checked yet.`,
        tone: 'neutral',
      };
    return {
      message: `${CHANGED}. The one comparison came after all five moves, so you cannot tell which move caused it: any of the five could have changed the paging answer, and you must search or undo them all.`,
      tone: 'warning',
    };
  }
  if (state.done < MISTAKE_MOVE)
    return {
      message: `Move ${state.done} made, then compared. ${SAME} ${state.done === 1 ? 'Move 1' : `Moves 1 to ${state.done}`} changed nothing the requests can see.`,
      tone: 'neutral',
    };
  return {
    message: `Move ${MISTAKE_MOVE} made, then compared. ${CHANGED}. The mistake is in move ${MISTAKE_MOVE}, and only move ${MISTAKE_MOVE} needs undoing: moves 1 and 2 compared the same. Its change is small enough to read: repository.py's paging query lost ORDER BY t.id.`,
    tone: 'success',
  };
}
